import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Star, MapPin, Clock, Award, CheckCircle2, TrendingUp, Crown, Eye } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ALL_CATEGORIES } from '@/data/mockData';
import { BookingButton } from '@/components/BookingModal';
import { HorizontalCarousel } from '@/components/HorizontalCarousel';
import { BusinessShowcaseCard } from '@/components/BusinessShowcaseCard';
import { directory } from '@/lib/directory';

const categoryLabel = (slug) => {
  const c = ALL_CATEGORIES.find((x) => x.slug === slug);
  return c ? c.name : (slug || '').replace(/-/g, ' ');
};

// Legacy card (mock-data shape) still used by City / Country / Search pages.
export const BusinessCard = ({ b }) => {
  const navigate = useNavigate();
  const open = () => navigate(`/business/${b.slug || b.id}`);
  return (
    <article
      onClick={open}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && open()}
      role="link"
      tabIndex={0}
      className="group cursor-pointer rounded-2xl border border-gray-200 bg-card text-card-foreground shadow overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 w-72 sm:w-80 h-full flex flex-col focus:outline-none focus:ring-2 focus:ring-[#00AA6C]"
      data-testid={`business-card-${b.id}`}
    >
      <div className="relative h-44 overflow-hidden">
        <img alt={b.name} loading="lazy" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" src={b.image} />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
        <div className="absolute top-2 left-2 flex flex-col gap-1.5">
          {b.premium && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold shadow-md bg-gradient-to-r from-amber-500 to-amber-600 text-white" data-testid={`card-premium-${b.id}`}>
              <Crown className="h-3 w-3 mr-1" />Premium
            </div>
          )}
          {b.featured && (
            <div className="inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold shadow bg-[#D32323] text-white">
              <Award className="h-3 w-3 mr-1" />Featured
            </div>
          )}
        </div>
        {b.verified && (
          <div className="absolute top-2 right-2 inline-flex items-center rounded-full px-2.5 py-0.5 text-[10px] font-semibold shadow bg-[#00AA6C] text-white" data-testid={`card-verified-${b.id}`}>
            <CheckCircle2 className="h-3 w-3 mr-1" />Verified
          </div>
        )}
        <div className="absolute bottom-2 left-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/95 backdrop-blur text-xs font-semibold shadow">
          <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" aria-hidden />
          {b.rating} <span className="text-gray-500 font-normal">({b.reviews})</span>
        </div>
      </div>
      <div className="p-4 flex flex-col flex-1">
        <h3 className="font-heading text-base font-semibold text-gray-900 line-clamp-1 group-hover:text-[#00AA6C] transition-colors">{b.name}</h3>
        <div className="flex items-center text-xs text-gray-500 mt-1">
          <MapPin className="h-3 w-3 mr-1 flex-shrink-0" aria-hidden />
          <span className="line-clamp-1">{b.address}</span>
        </div>
        <div className="flex items-center text-xs mt-1.5">
          <Clock className="h-3 w-3 mr-1 flex-shrink-0 text-gray-400" aria-hidden />
          {b.status === 'open'
            ? <span className="text-green-600 font-medium">Open Now</span>
            : <span className="text-red-600 font-medium">Closed</span>}
        </div>
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100 gap-2">
          <div className="flex items-center space-x-2 min-w-0">
            <Badge variant="outline" className="capitalize text-[10px] font-semibold">{categoryLabel(b.category)}</Badge>
            <span className="text-xs font-medium text-[#00AA6C]">{b.price}</span>
          </div>
          <Button variant="green" size="sm" onClick={(e) => { e.stopPropagation(); open(); }} data-testid={`card-quickview-${b.id}`}>
            <Eye className="w-3.5 h-3.5" /> Quick view
          </Button>
        </div>
        <div className="mt-2">
          <BookingButton business={b} label="Book" testid={`card-book-${b.id}`} className="w-full" />
        </div>
      </div>
    </article>
  );
};

// Top-Rated Featured Businesses — REAL businesses only (no beaches / attractions /
// nature). Ranked Premium > Verified > Featured > Rating > Reviews on the backend.
export const TopRatedBusinesses = () => {
  const [items, setItems] = useState(null);

  useEffect(() => {
    let alive = true;
    directory.featuredBusinesses(12)
      .then((d) => { if (alive) setItems(d.items || []); })
      .catch(() => { if (alive) setItems([]); });
    return () => { alive = false; };
  }, []);

  if (items && items.length === 0) return null;

  return (
    <section className="py-16 bg-gray-50" data-testid="top-rated-businesses">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex items-end justify-between mb-6 gap-4">
          <div>
            <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-[#00AA6C] mb-1">
              <Star className="w-3.5 h-3.5 fill-current" /> Top Rated
            </div>
            <h2 className="font-heading text-3xl font-bold text-gray-900" data-testid="top-rated-title">Top Rated Featured Businesses</h2>
            <p className="text-gray-600 mt-1 text-sm">Hand-picked businesses loved by travelers</p>
          </div>
          <Button variant="green" size="sm" asChild className="shrink-0">
            <Link to="/search" data-testid="top-rated-explore-all"><TrendingUp className="w-4 h-4" /> Explore All</Link>
          </Button>
        </div>
        {!items ? (
          <div className="flex gap-6">
            {[0, 1, 2, 3].map((i) => <div key={i} className="w-72 sm:w-80 h-64 rounded-2xl bg-gray-100 animate-pulse shrink-0" />)}
          </div>
        ) : (
          <HorizontalCarousel testid="top-rated-carousel">
            {items.map((b) => <BusinessShowcaseCard key={b.id} b={b} />)}
          </HorizontalCarousel>
        )}
      </div>
    </section>
  );
};
